import type { DmPatient } from '../../../../../services/dmPatient';

export type MonitoringState = 'active' | 'paused' | 'stopped' | 'not_started';

export function monitoringState(status: string | null | undefined): MonitoringState {
    const value = (status ?? '').toLowerCase();
    if (value === 'started' || value === 'resumed') return 'active';
    if (value === 'paused') return 'paused';
    if (value === 'stopped') return 'stopped';
    return 'not_started';
}

const DAY_MS = 86_400_000;
const LONG_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const SHORT_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** "Jun 16, 2026" for an ISO timestamp, like DM's own panel. */
export function formatDmDate(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', LONG_DATE);
}

/** Local yyyy-mm-dd, the date input's format; toISOString would shift late evenings a day. */
export function toLocalYmd(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

/** Local yyyy-mm-dd of an ISO timestamp, or null. */
export function isoToYmd(iso: string | null | undefined): string | null {
    return iso ? toLocalYmd(new Date(iso)) : null;
}

/** yyyy-mm-dd read as a local date; `new Date('yyyy-mm-dd')` would read it as UTC. */
function parseYmd(ymd: string): Date {
    const [year, month, day] = ymd.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Today plus `days`, as yyyy-mm-dd. */
export function ymdInDays(days: number): string {
    const date = startOfToday();
    date.setDate(date.getDate() + days);
    return toLocalYmd(date);
}

export function todayYmd(): string {
    return ymdInDays(0);
}

/** Whole local days from today to a yyyy-mm-dd date. */
export function daysFromToday(ymd: string): number {
    return Math.round((parseYmd(ymd).getTime() - startOfToday().getTime()) / DAY_MS);
}

/** "Sep 29, 2026" for a yyyy-mm-dd date. */
export function formatYmd(ymd: string | null | undefined): string {
    return ymd ? parseYmd(ymd).toLocaleDateString('en-US', LONG_DATE) : '';
}

/** "Sep 29" for the day `days` from today. */
export function shortDateInDays(days: number): string {
    return parseYmd(ymdInDays(days)).toLocaleDateString('en-US', SHORT_DATE);
}

export function errorText(error: unknown): string {
    return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

/** The DM patient's full name, or undefined when DM has neither part. */
export function fullName(patient: {
    firstName: string | null;
    lastName: string | null;
}): string | undefined {
    const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
    return name || undefined;
}

export interface TreatmentLine {
    label: string;
    text: string;
}

/** DM's treatment per arch, merged into one "Upper/Lower" line when both arches match. */
export function treatmentLines({
    upper,
    lower
}: NonNullable<DmPatient['treatment']>): TreatmentLine[] {
    if (upper && upper === lower) return [{ label: 'Upper/Lower', text: upper }];
    return [
        { label: 'Upper', text: upper },
        { label: 'Lower', text: lower }
    ].filter((line): line is TreatmentLine => Boolean(line.text));
}
